import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // User Authentication State
      user: null,
      userToken: null,
      isUserAuthenticated: false,

      // Etsy Authentication State
      etsyTokens: {
        accessToken: null,
        refreshToken: null,
        expiresAt: null,
        tokenType: 'Bearer',
      },
      etsyUserInfo: null,
      etsyShopInfo: null,
      isEtsyConnected: false,

      // Server-authoritative Etsy connection state (#369). One of
      // 'unknown' | 'connected' | 'reconnect_required' | 'disconnected' |
      // 'never_connected', straight from /third-party/connection-status.
      //
      // isEtsyConnected is a BOOLEAN and therefore cannot express
      // "reconnect_required", which is why a seller whose grant died looked
      // exactly like a seller who never connected. Two writers below used to
      // raise the boolean from purely local evidence (a localStorage token,
      // a rate-limit response); both now refuse to do so while this says
      // reconnect_required, so the boolean can never contradict the server.
      etsyConnectionState: 'unknown',
      etsyReconnectSince: null,
      etsyReconnectDetail: null,
      etsyReconnectShops: [],

      // Rate-limit state for the /verify-connection endpoint.
      // When the backend sees Etsy return 429, it returns
      // { connected: true, rate_limited: true, retry_after, stale }.
      // The connection is still alive; we just couldn't verify
      // against Etsy right now. UI should show a transient banner,
      // NOT the reconnect prompt. Transient — not persisted.
      etsyRateLimited: false,
      etsyStale: false,
      etsyRetryAfter: null,

      // Timestamp of the last completed verify-connection round-trip.
      // Used by useAuth to skip back-to-back verifies when auth state
      // jitter (login flow, token refresh) re-triggers the effect
      // multiple times in quick succession. Transient — not persisted.
      etsyLastVerifiedAt: null,

      // Multi-shop state
      etsyStores: [], // All connected Etsy stores
      shopifyStores: [], // All connected Shopify stores
      activeEtsyStore: null, // Currently selected Etsy store { id, etsy_shop_id, shop_name }
      activeShopifyStore: null, // Currently selected Shopify store { id, shop_domain, shop_name }

      // Loading States
      userLoading: false,
      etsyLoading: false,

      // Error States
      userError: null,
      etsyError: null,

      // User Authentication Actions
      setUserAuth: (user, token) =>
        set({
          user,
          userToken: token,
          isUserAuthenticated: true,
          userError: null,
        }),

      // Merge partial fields into the persisted user object (e.g. fresh AI
      // preference flags returned by PATCH /users/me/profile). No-op when no
      // user is logged in. Keeps components that re-init local state from
      // `user` (Account Settings toggles) consistent after a save.
      updateUser: fields => set(state => (state.user ? { user: { ...state.user, ...fields } } : {})),

      clearUserAuth: () =>
        set({
          user: null,
          userToken: null,
          isUserAuthenticated: false,
          userError: null,
        }),

      setUserToken: token => set({ userToken: token }),
      setUserLoading: loading => set({ userLoading: loading }),
      setUserError: error => set({ userError: error }),

      // Etsy Authentication Actions
      setEtsyTokens: tokenData => {
        const tokens = {
          accessToken: tokenData.access_token || tokenData.accessToken,
          refreshToken: tokenData.refresh_token || tokenData.refreshToken,
          expiresAt: tokenData.expires_at || Date.now() + (tokenData.expires_in || 3600) * 1000,
          tokenType: tokenData.token_type || 'Bearer',
        };

        set(state => ({
          etsyTokens: tokens,
          etsyError: null,
          // Optimistically connected on a local access token — EXCEPT when the
          // server has already told us the grant is dead. A token in
          // localStorage says nothing about whether Etsy still honours it, and
          // raising the flag here is what let a revoked seller keep seeing a
          // green UI after every reload.
          isEtsyConnected: state.etsyConnectionState === 'reconnect_required' ? false : !!tokens.accessToken,
        }));
      },

      setEtsyConnection: (userInfo, shopInfo) =>
        set({
          etsyUserInfo: userInfo,
          etsyShopInfo: shopInfo,
          isEtsyConnected: true,
          // A live verify beat whatever the DB state said — this IS the
          // authoritative answer, so the banner must clear with it.
          etsyConnectionState: 'connected',
          etsyReconnectSince: null,
          etsyReconnectDetail: null,
          etsyReconnectShops: [],
          etsyError: null,
          // A clean (non-rate-limited) verify clears any prior
          // rate-limited markers — we just got a fresh, authoritative
          // success.
          etsyRateLimited: false,
          etsyStale: false,
          etsyRetryAfter: null,
          // Successful verify just completed → bookmark for the
          // skip-recent-verify guard in useAuth.
          etsyLastVerifiedAt: Date.now(),
        }),

      // Sets the rate-limited markers WITHOUT touching connection state.
      // Called when verify-connection returns rate_limited: true. If
      // user_info / shop_info are present (LKG fallback), they're applied
      // so the UI shows the seller's previously-verified shop info while
      // a banner indicates the data is stale.
      setEtsyRateLimited: ({ retryAfter, stale, userInfo, shopInfo }) =>
        set(state => ({
          etsyRateLimited: true,
          etsyStale: !!stale,
          etsyRetryAfter: typeof retryAfter === 'number' ? retryAfter : null,
          // Preserve existing connection unless we have fresher LKG data.
          etsyUserInfo: userInfo || state.etsyUserInfo,
          etsyShopInfo: shopInfo || state.etsyShopInfo,
          // The token is still valid — Etsy's just rate-limiting us. Unless
          // the server already told us it is NOT: a 429 says nothing about the
          // grant, so it must not overturn a known revocation. Given the
          // app-wide 5 QPS / 5,000 QPD cap this is the likely real-world path.
          isEtsyConnected: state.etsyConnectionState !== 'reconnect_required',
          etsyError: null,
          // We did get a (degraded) response from the backend, so this
          // counts as a verify for the skip-recent guard. Without this,
          // the next render would re-fire and we'd burn another verify
          // request that we already know will be rate-limited.
          etsyLastVerifiedAt: Date.now(),
        })),

      // Applies the server's DB-only answer from /third-party/connection-status.
      // This is the ONLY writer of etsyConnectionState that can raise
      // reconnect_required, and it drives isEtsyConnected rather than the
      // other way round.
      setEtsyConnectionState: payload =>
        set({
          etsyConnectionState: payload?.state || 'unknown',
          etsyReconnectSince: payload?.since || null,
          etsyReconnectDetail: payload?.detail || null,
          etsyReconnectShops: payload?.shops || [],
          isEtsyConnected: !!payload?.connected,
        }),

      clearEtsyAuth: () =>
        set(state => ({
          etsyTokens: {
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            tokenType: 'Bearer',
          },
          etsyUserInfo: null,
          etsyShopInfo: null,
          isEtsyConnected: false,
          etsyError: null,
          etsyStores: [],
          activeEtsyStore: null,
          etsyRateLimited: false,
          etsyStale: false,
          etsyRetryAfter: null,
          // Resetting on disconnect so a future reconnect doesn't
          // inherit a stale "verified at" timestamp from before.
          etsyLastVerifiedAt: null,
          // etsyConnectionState is deliberately NOT reset here. This clears
          // LOCAL credentials; it says nothing about what the server observed.
          // getValidEtsyToken() calls it the moment a token refresh fails —
          // precisely when the server has just told us reconnect_required — so
          // blanking it would erase the one piece of state that distinguishes
          // a revoked seller from a stranger. Only setEtsyConnectionState (the
          // server's answer) and setEtsyConnection (a live verify) move it.
        })),

      setEtsyLoading: loading => set({ etsyLoading: loading }),
      setEtsyError: error => set({ etsyError: error }),

      // Multi-shop actions
      setEtsyStores: stores => {
        const current = get();
        set({ etsyStores: stores });
        // Auto-select first store if none selected
        if (!current.activeEtsyStore && stores.length > 0) {
          set({ activeEtsyStore: stores[0] });
        }
        // Clear active if it no longer exists
        if (current.activeEtsyStore && !stores.find(s => s.id === current.activeEtsyStore.id)) {
          set({ activeEtsyStore: stores.length > 0 ? stores[0] : null });
        }
      },

      setActiveEtsyStore: store => set({ activeEtsyStore: store }),

      setShopifyStores: stores => {
        const current = get();
        set({ shopifyStores: stores });
        if (!current.activeShopifyStore && stores.length > 0) {
          set({ activeShopifyStore: stores[0] });
        }
        if (current.activeShopifyStore && !stores.find(s => s.id === current.activeShopifyStore.id)) {
          set({ activeShopifyStore: stores.length > 0 ? stores[0] : null });
        }
      },

      setActiveShopifyStore: store => set({ activeShopifyStore: store }),

      // Helper Methods
      isEtsyTokenValid: () => {
        const { etsyTokens } = get();

        if (!etsyTokens.accessToken) {
          return false;
        }

        if (!etsyTokens.expiresAt) {
          return true; // No expiry, assume valid
        }

        // Check if token expires within next 5 minutes (buffer)
        const bufferTime = 5 * 60 * 1000;
        return Date.now() < etsyTokens.expiresAt - bufferTime;
      },

      getValidEtsyToken: async () => {
        const { etsyTokens, isEtsyTokenValid, refreshEtsyToken } = get();

        if (isEtsyTokenValid()) {
          return etsyTokens.accessToken;
        }

        // Token is expired - try to refresh it
        if (etsyTokens.refreshToken) {
          const refreshResult = await refreshEtsyToken();
          if (refreshResult.success) {
            return refreshResult.accessToken;
          }
        }

        // If refresh failed or no refresh token, clear auth
        get().clearEtsyAuth();
        return null;
      },

      refreshEtsyToken: async () => {
        const { etsyTokens, setEtsyTokens, setEtsyError } = get();

        if (!etsyTokens.refreshToken) {
          return { success: false, error: 'No refresh token available' };
        }

        try {
          // Call auth service to refresh token
          const authService = await import('../services/authService');
          const result = await authService.default.refreshEtsyToken(etsyTokens.refreshToken);

          if (result.success && result.access_token) {
            setEtsyTokens(result);
            return {
              success: true,
              accessToken: result.access_token,
            };
          } else {
            setEtsyError('Failed to refresh Etsy token');
            return { success: false, error: result.error || 'Refresh failed' };
          }
        } catch (error) {
          console.error('❌ Error refreshing Etsy token:', error);
          setEtsyError(error.message || 'Token refresh failed');
          return { success: false, error: error.message };
        }
      },

      // Debug method
      getDebugInfo: () => {
        const state = get();
        return {
          userAuth: {
            hasUser: !!state.user,
            hasToken: !!state.userToken,
            isAuthenticated: state.isUserAuthenticated,
            userEmail: state.user?.email || 'N/A',
          },
          etsyAuth: {
            hasAccessToken: !!state.etsyTokens.accessToken,
            hasRefreshToken: !!state.etsyTokens.refreshToken,
            isConnected: state.isEtsyConnected,
            tokenValid: state.isEtsyTokenValid(),
            expiresAt: state.etsyTokens.expiresAt ? new Date(state.etsyTokens.expiresAt) : null,
            accessTokenPreview: state.etsyTokens.accessToken
              ? state.etsyTokens.accessToken.substring(0, 20) + '...'
              : 'None',
            refreshTokenPreview: state.etsyTokens.refreshToken
              ? state.etsyTokens.refreshToken.substring(0, 20) + '...'
              : 'None',
            canRefresh: !!state.etsyTokens.refreshToken,
            hasUserInfo: !!state.etsyUserInfo,
            hasShopInfo: !!state.etsyShopInfo,
            loading: state.etsyLoading,
            error: state.etsyError,
          },
          timestamps: {
            currentTime: new Date().toISOString(),
            tokenExpiresIn: state.etsyTokens.expiresAt
              ? Math.max(0, Math.floor((state.etsyTokens.expiresAt - Date.now()) / 1000 / 60)) + ' minutes'
              : 'N/A',
          },
        };
      },
    }),
    {
      name: 'auth-store', // localStorage key
      partialize: state => ({
        // Only persist these fields
        user: state.user,
        userToken: state.userToken,
        isUserAuthenticated: state.isUserAuthenticated,
        etsyTokens: state.etsyTokens,
        etsyUserInfo: state.etsyUserInfo,
        etsyShopInfo: state.etsyShopInfo,
        // isEtsyConnected is deliberately NOT persisted. Restoring a stale
        // `true` from localStorage on every reload is how a revoked seller
        // kept being shown a connected UI before the first status round-trip
        // landed. etsyConnectionState carries the same information WITH the
        // reconnect_required case, and the server re-confirms it on boot.
        etsyConnectionState: state.etsyConnectionState,
        etsyReconnectSince: state.etsyReconnectSince,
        etsyReconnectDetail: state.etsyReconnectDetail,
        // Persisted so the bootstrap verify skip-window survives full page
        // loads — without it every reload re-fired verify-connection.
        etsyLastVerifiedAt: state.etsyLastVerifiedAt,
        // Multi-shop persistence
        etsyStores: state.etsyStores,
        shopifyStores: state.shopifyStores,
        activeEtsyStore: state.activeEtsyStore,
        activeShopifyStore: state.activeShopifyStore,
      }),
      // isEtsyConnected is DERIVED on rehydrate rather than restored, so the
      // boolean and the state can never disagree across a reload. Only
      // 'connected' brings the flag back up; a persisted 'reconnect_required'
      // rehydrates as disconnected and keeps its banner.
      merge: (persisted, current) => ({
        ...current,
        ...persisted,
        isEtsyConnected: persisted?.etsyConnectionState === 'connected',
      }),
    }
  )
);

export default useAuthStore;
